import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaGlobe } from 'react-icons/fa';
import { CiMail } from "react-icons/ci";
import '../styles/LoginForm.css';
import authService from '../services/authService';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { t, i18n } = useTranslation();

  const languages = {
    'en': { name: 'English', flag: '/flags/en.png' },
    'cs': { name: 'Čeština', flag: '/flags/cs.png' }
  };

  // Helper function to determine error type and message
  const getErrorMessage = (error) => {
    // Check if it's a network error (server down)
    if (!error.response) {
      if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
        return t('forgotPassword.errors.serverDown', 'Server is currently unavailable. Please try again later.');
      }
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        return t('forgotPassword.errors.timeout', 'Connection timeout. Please check your internet connection and try again.');
      }
      // Generic network error
      return t('forgotPassword.errors.networkError', 'Unable to connect to server. Please check your internet connection.');
    }

    // Server responded with an error
    const status = error.response.status;
    const data = error.response.data;

    switch (status) {
      case 404:
        return t('forgotPassword.errors.emailNotFound', 'No account found with this email address.');
      case 400:
        if (data && typeof data === 'string') {
          return data;
        }
        if (data && data.message) {
          return data.message;
        }
        return t('forgotPassword.errors.invalidEmail', 'Please enter a valid email address.');
      case 429:
        return t('forgotPassword.errors.tooManyAttempts', 'Too many password reset attempts. Please wait before trying again.');
      case 500:
        return t('forgotPassword.errors.serverError', 'Server error occurred. Please try again later.');
      case 502:
      case 503:
      case 504:
        return t('forgotPassword.errors.serverUnavailable', 'Server is temporarily unavailable. Please try again later.');
      default:
        // Try to use server message if available
        if (typeof data === 'string') {
          return data;
        }
        if (data && data.message) {
          return data.message;
        }
        return t('forgotPassword.errors.general', 'An error occurred while sending password reset email. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await authService.forgotPassword(email);
      setIsSuccess(true);
      toast.success(t('forgotPassword.success', 'Password reset email sent successfully! Please check your inbox.'));
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMessage = getErrorMessage(error);
      toast.error(errorMessage);
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLanguageChange = (lang) => {
    i18n.changeLanguage(lang);
    setLanguageDropdownOpen(false);
  };

  const toggleLanguageDropdown = () => {
    setLanguageDropdownOpen(!languageDropdownOpen);
  };

  return (
    <div className="login-container">
      <div className="login-language-selector">
        <button
          onClick={toggleLanguageDropdown}
          className="language-toggle-btn"
          aria-label="Change language"
        >
          <FaGlobe />
        </button>
        {languageDropdownOpen && (
          <div className="language-dropdown">
            {Object.entries(languages).map(([code, { name, flag }]) => (
              <div
                key={code}
                onClick={() => handleLanguageChange(code)}
                className="language-option"
              >
                <img
                  src={flag}
                  alt={`${name} flag`}
                  className="language-flag"
                />
                {name}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="login-header">
        <h2>Scoreo</h2>
        <h3>{t('forgotPassword.title')}</h3>
        <p>{t('forgotPassword.info')}</p>
      </div>

      <div className="login-card-wrapper">
        <div className="login-card">
          {isSuccess ? (
            <div className="success-message-container">
              <p className="success-message">{t('forgotPassword.success', 'Password reset email sent successfully! Please check your inbox.')}</p>
              <Link to="/login" className="btn-primary">{t('backToLogin')}</Link>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit}>
                <div className="input-group">
                  <label htmlFor="email">Email *</label>
                  {/* Nové umístění odkazu */}
                  <div className="forgot-password">
                    <Link to="/login">{t('backToLogin')}</Link>
                  </div>
                  <div className="input-wrapper">
                    <CiMail className="input-icon" />
                    <input
                      type="email"
                      id="email"
                      placeholder="jan.novak@gmail.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={isLoading}
                    />
                  </div>
                </div>
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading ? t('sendingPasswordRecoveryLink') : t('sendPasswordRecoveryLink')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;